import React from "react";
import { motion } from "framer-motion";

/**
 * Industries We Serve — 9 (or N) matte-black dark cards with orange radial
 * glow on hover. Identical visual DNA to Applications variant="dark",
 * but with the traditional "Industries We Serve" section framing.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Industries We Serve")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { Icon, title, desc }[]
 *  - gridCols: string (default "lg:grid-cols-3")
 */
export default function IndustriesWeServe({
  testId,
  eyebrow = "Industries We Serve",
  heading,
  subtitle,
  items = [],
  gridCols = "lg:grid-cols-3",
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-matte-black py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.12]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2 text-liftile-cream">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-cream/70 mt-6 leading-relaxed">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 md:gap-7`}
        >
          {items.map((ind, i) => (
            <motion.div
              key={ind.title}
              data-testid={`${testId}-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.55,
                delay: i * 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative h-full rounded-2xl bg-white/[0.03] border border-white/[0.08] p-7 md:p-8 hover:border-liftile-orange/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,107,26,0.25) 0%, rgba(255,107,26,0) 70%)",
                  filter: "blur(30px)",
                }}
              />
              <div className="relative flex items-start gap-5">
                <div className="w-12 h-12 rounded-xl bg-liftile-orange/15 flex items-center justify-center shrink-0 group-hover:bg-liftile-orange transition-colors duration-500 p-3">
                  <ind.Icon
                    className="w-6 h-6 text-liftile-orange group-hover:text-white transition-colors duration-500"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <h3 className="font-heading text-base md:text-[17px] uppercase tracking-wider font-semibold text-liftile-cream leading-snug">
                    {ind.title}
                  </h3>
                  <p className="font-body text-sm text-liftile-cream/60 mt-2 leading-relaxed">
                    {ind.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
